import React, { useState } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Image, SafeAreaView, ActivityIndicator } from "react-native";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [step, setStep] = useState(1); // 1: Email entry, 2: Code verification and new password
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const { forgotPassword, resetPassword } = useAuth();
	const navigate = useNavigate();

	const handleRequestCode = async () => {
		if (!email) {
			setError("Please enter your email address");
			return;
		}

		setError("");
		setLoading(true);

		try {
			const success = await forgotPassword(email);
			if (success) {
				setStep(2);
			} else {
				setError("Failed to send verification code. Please try again.");
			}
		} catch (err) {
			setError(err.message || "An error occurred");
		} finally {
			setLoading(false);
		}
	};

	const handleResetPassword = async () => {
		if (!code) {
			setError("Please enter the verification code");
			return;
		}

		if (!newPassword) {
			setError("Please enter a new password");
			return;
		}

		// Password validation
		if (newPassword.length < 10) {
			setError("Password must be at least 10 characters long");
			return;
		}
		if (!/[a-z]/.test(newPassword) || !/[A-Z]/.test(newPassword)) {
			setError("Password must contain both upper and lower case letters");
			return;
		}
		if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
			setError("Password must contain at least one symbol");
			return;
		}

		setError("");
		setLoading(true);

		try {
			const success = await resetPassword(email, code, newPassword);
			if (success) {
				navigate("/login", { state: { passwordReset: true } });
			} else {
				setError("Failed to reset password. Please try again.");
			}
		} catch (err) {
			setError(err.message || "An error occurred");
		} finally {
			setLoading(false);
		}
	};

	return (
		<SafeAreaView style={styles.container}>
			<View style={styles.content}>
				{/* Logo */}
				<View style={styles.logoContainer}>
					<View style={styles.logoBox}>
						<Image source={require("../assets/logowhitetrans.png")} style={styles.logo} resizeMode="contain" />
					</View>
				</View>

				{/* Welcome Text */}
				<View style={styles.welcomeContainer}>
					<Text style={styles.welcomeTitle}>{step === 1 ? "Forgot Password" : "Reset Password"}</Text>
					<Text style={styles.welcomeSubtitle}>{step === 1 ? "Enter your email and we'll send you a verification code" : "Enter the verification code and your new password"}</Text>
					{error ? <Text style={styles.errorText}>{error}</Text> : null}
				</View>

				{/* Back Button */}
				<TouchableOpacity style={styles.backButton} onPress={() => (step === 1 ? navigate("/login") : setStep(1))}>
					<Text style={styles.backButtonText}>← Back</Text>
				</TouchableOpacity>

				{/* Form */}
				<View style={styles.form}>
					{step === 1 ? (
						<>
							<View style={styles.inputContainer}>
								<Text style={styles.label}>Email</Text>
								<TextInput style={styles.input} placeholder="Enter your email" placeholderTextColor="#666" value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" editable={!loading} />
							</View>

							<TouchableOpacity style={[styles.resetButton, loading && styles.buttonDisabled]} onPress={handleRequestCode} disabled={loading}>
								{loading ? <ActivityIndicator color="white" /> : <Text style={styles.resetButtonText}>Send Verification Code</Text>}
							</TouchableOpacity>
						</>
					) : (
						<>
							<View style={styles.inputContainer}>
								<Text style={styles.label}>Verification Code</Text>
								<TextInput style={styles.input} placeholder="Enter verification code" placeholderTextColor="#666" value={code} onChangeText={setCode} keyboardType="number-pad" editable={!loading} />
							</View>

							<View style={styles.inputContainer}>
								<Text style={styles.label}>New Password</Text>
								<TextInput style={styles.input} placeholder="Create new password" placeholderTextColor="#666" value={newPassword} onChangeText={setNewPassword} secureTextEntry editable={!loading} />
								<View style={styles.passwordRequirements}>
									<Text style={styles.requirementText}>• Upper & lower case letters</Text>
									<Text style={styles.requirementText}>• A symbol (#$%)</Text>
									<Text style={styles.requirementText}>• A minimum of 10 characters</Text>
								</View>
							</View>

							<TouchableOpacity style={[styles.resetButton, loading && styles.buttonDisabled]} onPress={handleResetPassword} disabled={loading}>
								{loading ? <ActivityIndicator color="white" /> : <Text style={styles.resetButtonText}>Reset Password</Text>}
							</TouchableOpacity>
						</>
					)}
				</View>
			</View>
		</SafeAreaView>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#000000",
	},
	content: {
		flex: 1,
		padding: 24,
		justifyContent: "center",
		maxWidth: 400,
		alignSelf: "center",
		width: "100%",
	},
	logoContainer: {
		alignItems: "center",
		marginBottom: 32,
	},
	logoBox: {
		width: 200,
		height: 48,
		backgroundColor: "transparent",
		borderRadius: 12,
		justifyContent: "center",
		alignItems: "center",
	},
	logo: {
		width: 180,
		height: 40,
	},
	welcomeContainer: {
		alignItems: "center",
		marginBottom: 32,
	},
	welcomeTitle: {
		fontSize: 24,
		fontWeight: "bold",
		color: "white",
		marginBottom: 8,
	},
	welcomeSubtitle: {
		fontSize: 16,
		color: "#666",
		textAlign: "center",
	},
	backButton: {
		position: "absolute",
		top: 24,
		left: 24,
	},
	backButtonText: {
		color: "#666",
		fontSize: 16,
	},
	form: {
		gap: 16,
	},
	inputContainer: {
		marginBottom: 16,
	},
	label: {
		color: "#666",
		marginBottom: 8,
		fontSize: 14,
	},
	input: {
		backgroundColor: "#1A1A1A",
		borderWidth: 1,
		borderColor: "#333",
		borderRadius: 6,
		padding: 12,
		color: "white",
		fontSize: 14,
	},
	passwordRequirements: {
		marginTop: 12,
	},
	requirementText: {
		color: "#666",
		fontSize: 14,
		marginBottom: 4,
	},
	resetButton: {
		backgroundColor: "#0066FF",
		padding: 16,
		borderRadius: 6,
		alignItems: "center",
		marginBottom: 16,
	},
	resetButtonText: {
		color: "white",
		fontSize: 14,
		fontWeight: "500",
	},
	errorText: {
		color: "#ff4444",
		marginTop: 8,
		fontSize: 14,
		textAlign: "center",
	},
	buttonDisabled: {
		opacity: 0.7,
	},
});

export default ForgotPassword;
